<template >
    <div class="container-orgs card">
        
            <div class="org-container">
                <div class="container">
                    <div class="d-flex justify-content-between align-items-center">
                        <h1 class="title-org">{{ $t('project.form.configuration.organizations') }}</h1>
                        <div class="form-group search-input w-25">
                            <div class="form-control">
                                <input 
                                    type="search" 
                                    id="main-search" 
                                    class="input-ui border"
                                    :placeholder="$t('search')+'..'"
                                    ref="tagSearchInput"
                                    v-model="searchQuery"
                                    @search="queryTags"
                                    v-on:keyup="onSearchChange"
                                    :disabled="disabled"
                                />
                                <span class="fa fa-search"></span>
                            </div>
                        </div>
                    </div>

                    <div class="admin-tags mt20">
                        <load-content :loading="project_setup_is_busy || projectCategorySetupIsLoading">
                            <template v-if="isUseOrgWithCategory()">
                                <tabs
                                    :tabs="projectSetupDataTabs"
                                    :currentTabValue="currentTabValue"
                                    wrapper-class="organization-tabs"
                                    tab-class="organization-tabs__item"
                                    tab-active-class="organization-tabs__item_active"
                                    line-class="organization-tabs__active-line"
                                    :onClick="handleClick"
                                />
                            </template>
                            <table-organization :tagId="currentTabValue" :rows="filteredOrganizations" />
                            <button v-if="canLoadMore" class="hasMore btn btn-sm btn-carma w-25 text-center m-auto" @click.prevent="loadMore" :disabled="isBusy">
                                <div v-if="isBusy" class="text-center">
                                    <loader-inline class="gray sm" />
                                </div>
                                <span v-else>
                                    <span>{{ $t('project.form.configuration.load_more') }}</span>
                                </span>
                            </button>
                        </load-content>
                    </div>
                </div>
            </div>
        
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import LoadContent from "../load-content";
import TableOrganization from "./organizations/table";
import Tabs from '../vue-tabs-with-active-line';
import loaderInline from '../loader-inline.vue';
import isEqual from 'lodash/isEqual';
import FormHelper from '@/helpers/form';

export default {
    name: "Organizations",
    components: {
        LoadContent,
        TableOrganization,
        Tabs,
        loaderInline
    },
    data: function () {
        return {
            currentTabValue: null,
            projectSetupDataTabs: [],
            organizations_category_id: 0,
            filteredOrganizations: [],
            searchQuery: '',
            globalTimeout: null
        }
    },
    computed: {
        ...mapState({
            organizations: state => state?.organizations?.organizations,
            projectSetup: state => state?.projectSetup?.projectSetup?.data,
            projectSetupGeneral: state => state?.projectSetup?.projectSetup?.data?.general,
            projectCategorySetup: state => state.projectSetup.projectCategorySetup?.data || [],
            projectCategorySetupIsLoading: state => state.projectSetup?.projectCategorySetup?.isBusy,
        }),
        ...mapGetters({project_setup_is_busy: 'projectSetup/project_setup_is_busy'}),
        organizationsData() { return this.organizations?.data?.data },
        pagination() { return this.organizations?.data?.pagination },
        isBusy() { return this.organizations.isBusy },
        canLoadMore() {
            return this.pagination?.currentPage && this.pagination?.currentPage < this.pagination?.totalPages 
        },
        disabled() {
          return (this.isBusy || this.project_setup_is_busy || this.projectCategorySetupIsLoading) && (this.organizationsData.length === 0);
        }
    },
    watch: {
        projectSetupGeneral(newValue, oldValue) {
            // reset tabs on toggle or category id change
            if(
                newValue?.organizations_with_category !== oldValue?.organizations_with_category ||
                newValue?.organizations_category_id !== oldValue?.organizations_category_id
            ){
                this.orgTabs();
                this.queryTags();
            }
        },
        organizationsData(newValue, oldValue) {
            if(newValue && (!isEqual(newValue, oldValue) || !this.filteredOrganizations?.length)) this.filteredOrganizations = newValue;
        },
        projectCategorySetup() {
            // reset tabs on category api resp received
            this.orgTabs();
        },
    },
    created: function() {
        this.getprojectSetup({ projectUid: this.$route.params.project_uid }).then(data => {
            if(!data?.general?.organizations_with_category) {
                this.loadMore();
                return;
            }
            this.getProjectCategoriesSetup({ projectUid: this.$route.params.project_uid })?.then(() => {
                this.queryTags();
                this.orgTabs();
            });
        });
    },
    methods: {
        ...mapActions({
            "getAllOrganizations": "organizations/getAllOrganizations", 
            "loadOrganizations": "organizations/loadOrganizations", 
            "getprojectSetup": "projectSetup/getprojectSetup",
            "getProjectCategoriesSetup": "projectSetup/getProjectCategoriesSetup"
        }),
        isUseOrgWithCategory() { return this.projectSetupGeneral?.organizations_with_category },
        orgTabs() {
            this.organizations_category_id = this.projectSetupGeneral?.organizations_category_id;
            // return early if data not proper
            if(!this.projectSetupGeneral?.organizations_with_category || !this.organizations_category_id) {
                this.currentTabValue = null;
                return [];
            }
            const _cate = this.projectCategorySetup?.find(c => c?.id === this.organizations_category_id);
            if(!_cate) return [];
            // prepare data for tabs component
            this.projectSetupDataTabs = _cate?.tags?.map((c, i) => {
                c.value = c?.id;
                c.title = c?.name;
                c.order = c?.display_order || i+1;
                return c;
            });
            // default to the 1st option/tab
            this.currentTabValue = this.projectSetupDataTabs?.[0]?.value;
            return this.projectSetupDataTabs;
        },
        handleClick(tab) {
            this.currentTabValue = tab?.value;
            this.queryTags();
        },
        getTabCategories(currentTabValue = this.currentTabValue){
            if(!this.isUseOrgWithCategory()) return [];
            return this?.organizationsData?.filter(org => org?.parent_tag_id === currentTabValue && (org.name = org.name || org?.tag_name) && (org.id = org.id || org?.tag_id)) || [];
        },
        async loadMore({ page, search_query = this.searchQuery?.trim() } = {}) {
            return await this.loadOrganizations({
                projectId: this.$route.params.project_uid,
                pagination: {
                    page: page || this.pagination.currentPage + 1,
                    perPage: 100,
                    category_id: this.currentTabValue,
                    order: 'name',
                    sort: 'asc',
                    search_query
                }
            });
        },
        onSearchChange() {
          FormHelper.delayFunction(() => this.queryTags(), 500, this);
        },
        async queryTags () {
            this.globalTimeout = null;
            await this.loadMore({ page: 1, search_query: this.searchQuery?.trim() });
        },
    }
}
</script>

<style lang="scss">
    .org-container {
        .admin-tags {
            position: relative;
            min-height: 60px;
        }
    }

    .organization-tabs {
        position: relative;
        margin-bottom: 2px;
        bottom: -3px;

        @media #{$medium-screen} {
            margin-bottom: 10px;
            bottom: -12px;
        }

        &__item {
            display: inline-block;
            padding: 3px 8px;
            margin: 0 1px;
            background-color: transparent;
            border-bottom: 3px solid transparent;
            font-size: .9em;
            cursor: pointer;
            transition: all 0.25s;
            white-space: nowrap;
            text-transform: uppercase;

            @media #{$medium-screen} {
                margin: 0 10px;
            }

            @media #{$large-screen} {
                margin: 0 20px;
            }

            &_active, &.active {
                color: #ec9148;
                border-bottom: 3px solid $vis-primary-color !important;
            }

            &:hover {
                border-bottom: 3px solid lighten($vis-primary-color, 15%);
                color: #ec9148;
            }
            &:focus {
                outline: none;
                border-bottom: 2px solid $vis-primary-color;
                color: $vis-primary-color;
            }
            &:first-child {
                margin-left: 0;
            }
            &:last-child {
                margin-right: 0;
            }
        }
    }
</style>